import {Alert, Box, Button, CircularProgress, Stack, Typography, useTheme} from "@mui/material";
import {useFlag, ZonedDateTime} from "@variocube/app-ui";
import {isNull} from "lodash";
import React, {useMemo, useState} from "react";
import {BoxedInfo} from "../../../component/boxed-info";
import {useBooking} from "../../../context/booking";
import {
	type BookingBoxes,
	BookingEventType,
	LockStatus,
	useBookingClient,
	useBookingEvent,
} from "../../../data/bookings";
import {B2Occupancy} from "../../../data/client.gen";
import {getZoneDateTimeNow, parseZoneDateTimeFromInstant} from "../../../dates-utils";
import {useLocalization} from "../../../i18n";
import {notBlank} from "../../../utils";
import {bookedBoxDemand, CubeSelectionDialog, useAutoOccupy, useCubeSelection} from "../booking-occupancy";
import {BookingInfoButton} from "./booking-info";
import {BookingOpenButton} from "./booking-open";

let boxOpeningTimeout: ReturnType<typeof setTimeout>;

export function BookingView() {
	const {t} = useLocalization();
	const {booking, bookingFrom, bookingUntil, isBookingActive, openBoxes} = useBooking();

	const [boxOpened, setBoxOpened] = useFlag(false);
	const [boxOpeningError, setBoxOpeningError, clearBoxOpeningError] = useFlag(false);
	const [occupancies, setOccupancies] = useState<B2Occupancy[]>(booking?.occupancies ?? []);

	const cubeSelection = useCubeSelection({occupancies, setOccupancies});

	const zoneId = booking?.site.zoneId ?? "UTC";
	const now = getZoneDateTimeNow(zoneId);

	/**
	 * Booking is started when the booking from is before current time.
	 */
	const isStarted = useMemo(() => bookingFrom !== undefined && ZonedDateTime.compare(bookingFrom, now) < 1, [
		bookingFrom,
	]);

	/**
	 * Booking is ended when the booking until is before current time.
	 */
	const isEnded = useMemo(() => bookingUntil !== undefined && ZonedDateTime.compare(bookingUntil, now) < 1, [
		bookingUntil,
	]);

	/**
	 * Denotes whether the booking is eligible for the ending process. Requirements:
	 * - site must have `enabledBookingEndAfter2ndOpening` flag enabled
	 * - has at least 1 occupancy which was opened at least once
	 * - exceed the grace period of site configuration (if configured)
	 *
	 * Use this flag to warn the user of their booking will be automatically ended upon opening.
	 */
	const isEligibleForBookingEnd = useMemo(() => {
		if (booking !== undefined && booking.site.enabledBookingEndAfter2ndOpening) {
			console.group("Booking end after 2nd opening enabled on site.");
			const occupancy = occupancies.find(o => (o.openedTimes ?? 0) > 0);
			if (occupancy !== null) {
				console.info("Booking has occupancy that was opened at least once.");
				if (notBlank(occupancy?.occupiedFrom)) {
					let gracePeriod = booking.site.gracePeriodOf2ndOpening;
					if (isNull(gracePeriod)) {
						console.info("Grace period not configured on site. Booking end is effective immediately.");
						gracePeriod = 0;
					}
					const occupiedFrom = parseZoneDateTimeFromInstant(occupancy?.occupiedFrom as string, zoneId);
					const effectiveUntil = now.subtract({minutes: gracePeriod});
					if (ZonedDateTime.compare(occupiedFrom, effectiveUntil) < 1) {
						console.info(
							"Booking has past the grace period and is now eligible for ending after 2nd opening.",
						);
						return true;
					}
				}
			}
		}
		return false;
	}, [booking, occupancies]);

	// Auto-occupy on the server-computed {@code autoOccupyCubeId} via the shared hook, so this view and the standard
	// booking view make the exact same decision (activity guard, idempotency, marking the cube as tried). When it is
	// not possible (multi-cube site with no known cube) or the cube can't fully serve the booking, the customer picks
	// a locker bank via {@link CubeSelectionDialog}.
	const {autoOccupying: occupyLoading, autoOccupyError: occupyError} = useAutoOccupy({
		active: Boolean(isBookingActive),
		occupancies,
		setOccupancies,
		onOccupied: cubeSelection.markTried,
	});

	const {totalBoxes: totalBoxesCount, bookedTypes} = useMemo(
		() => bookedBoxDemand(booking?.boxes as BookingBoxes | undefined),
		[booking],
	);
	const isFulfilled = totalBoxesCount > 0 && occupancies.length >= totalBoxesCount;

	// Once the auto-occupy attempt has settled without fully serving the booking (cube full/partial, or no cube known
	// on a multi-cube site), offer the site's other locker banks.
	const showLockerbankSelection = isStarted && !isEnded && !isFulfilled && !occupyLoading
		&& !cubeSelection.occupyingCube;

	if (booking !== undefined) {
		const client = useBookingClient(booking.id);
		useBookingEvent(client, BookingEventType.B2OccupancyEvent, event => {
			console.info("Lock status changed on box ", event.boxNumber);
			if (event.lockStatus === LockStatus.Open) {
				setBoxOpened();
				clearBoxOpeningError();
				if (boxOpeningTimeout !== undefined) {
					clearTimeout(boxOpeningTimeout);
					console.info("Cleared box opening timeout.");
				}
			}
			setOccupancies(prev =>
				prev.map(o => {
					if (o.uuid === event.uuid) {
						o.lockStatus = event.lockStatus;
						o.openedTimes = event.openedTimes;
					}
					return o;
				})
			);
		});
	}

	async function handleOpenBox() {
		if (booking !== undefined) {
			try {
				await openBoxes();
				boxOpeningTimeout = setTimeout(() => {
					setBoxOpeningError();
				}, 5000);
			}
			catch (err) {
				console.error("Failed to open box(es)", err);
				setBoxOpeningError();
			}
		}
	}

	const theme = useTheme();

	return (
		<>
			{!boxOpened && (
				<>
					<Typography textAlign="center" variant="h1" gutterBottom>
						{t("booking.simplified.title")}
					</Typography>
					{!isStarted && (
						<Typography variant="subtitle1" textAlign="center">
							{t("booking.simplified.notYetStarted")}
						</Typography>
					)}
					{isStarted && (
						<>
							{isEnded && (
								<Typography variant="subtitle1" textAlign="center">
									{t("booking.simplified.ended")}
								</Typography>
							)}
							{!isEnded && (
								<Stack
									direction="column"
									gap={1}
								>
									<div>
										<BoxedInfo
											label={t("booking.simplified.box")}
											content={
												<>
													{occupyLoading && <CircularProgress size={20} />}
													<Typography fontWeight={600} fontSize="2.5rem" lineHeight="1em">
														{occupancies.map(o => o.boxNumber).join(", ")}
													</Typography>
												</>
											}
										/>
									</div>
									<Typography variant="subtitle1">
										<strong style={{color: theme.palette.error.main, textTransform: "uppercase"}}>
											{t("booking.simplified.warning.leading")}
										</strong>
										&nbsp;
										{t("booking.simplified.warning.trailing")}
									</Typography>
									<Typography variant="subtitle1">
										<strong style={{color: theme.palette.error.main, textTransform: "uppercase"}}>
											{t("booking.simplified.attention.leading")}
										</strong>
										&nbsp;
										{t("booking.simplified.attention.trailing")}
									</Typography>
									{occupyError && <Alert severity="error">{t("booking.occupy.error")}</Alert>}
									{showLockerbankSelection && (
										booking?.siteHasMultipleCubes
											? (
												<Alert
													severity="warning"
													action={
														<Button
															variant="contained"
															color="primary"
															onClick={cubeSelection.openCubeSelection}
														>
															{t("booking.autoOccupy.selectLockerbank")}
														</Button>
													}
												>
													{occupancies.length > 0 || notBlank(booking?.autoOccupyCubeId)
														? t("booking.autoOccupy.anotherLockerBank")
														: t("booking.autoOccupy.chooseLockerbank")}
												</Alert>
											)
											: (
												// Single-cube site whose only bank can't serve the booking — no other bank
												// to offer, so surface support instead of a picker that dead-ends.
												<Alert severity="warning">
													{t("booking.occupy.insufficientLockers")}
												</Alert>
											)
									)}
									{cubeSelection.cubesError && (
										<Alert severity="error">{t("booking.occupy.error")}</Alert>
									)}
									{boxOpeningError && <Alert severity="error">{t("boxes.open.error")}</Alert>}
								</Stack>
							)}
						</>
					)}
					<Box my={1} flexGrow={1} />
					{isStarted && !isEnded && (
						<>
							<BookingOpenButton
								disabled={occupancies.length === 0}
								releaseWarning={isEligibleForBookingEnd}
								onOpen={handleOpenBox}
							/>
							<Box my={1} />
						</>
					)}
					<BookingInfoButton />
				</>
			)}
			{boxOpened && (
				<>
					<Box my={1} flexGrow={1} />
					<Typography align="center" variant="h2">
						{isEligibleForBookingEnd && t("booking.simplified.boxOpening.farewellMessage")}
						{!isEligibleForBookingEnd && t("booking.simplified.boxOpening.successMessage")}
					</Typography>
					<Box my={1} flexGrow={1} />
					{!isEligibleForBookingEnd && (
						<Button
							variant="contained"
							color="primary"
							disableElevation
							onClick={() => location.reload()}
						>
							{t("booking.simplified.cta")}
						</Button>
					)}
				</>
			)}
			{/* Locker-bank selection opens in a dialog (full-screen on phones) while the customer picks a cube. */}
			<CubeSelectionDialog bookedTypes={bookedTypes} selection={cubeSelection} />
		</>
	);
}
